import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DeprecatedConfirmationDialogComponent, DeprecatedModalFormComponent } from '@app/components';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { AnalyticService, FileHelperService, NotifyService } from '@app/services';
import { BenefitPlan } from '@models/benefits/benefit-plan.model';
import { Benefit } from '@models/benefits/benefit.model';
import { BenefitPlan as EmployeeBenefitPlan } from '@models/employee/benefits/benefit-plan.model';
import { Employee } from '@models/employee/employee.model';
import { FeatureService } from '@app/services/feature.service';

@Component({
    selector: 'app-benefit-details-standard',
    templateUrl: './benefit-details-standard.template.html',
    styleUrls: ['./benefit-details-standard.style.scss'],
})
export class BenefitDetailsStandard implements OnInit {
    @ViewChild('disclaimerDialog', { static: true }) disclaimerDialog: DeprecatedConfirmationDialogComponent;
    @ViewChild('editBenefitsModal', { static: true }) editBenefitsModal: DeprecatedModalFormComponent;

    @Output() isLoading: EventEmitter<boolean> = new EventEmitter<boolean>();

    employee: Employee;
    employeeBenefitPlan: EmployeeBenefitPlan;

    editPermission: string[] = EmployeeBenefitPlan.permission.view;
    allowEdit = false;
    shouldShowCard = false;

    isUserUpdatingExistingPlan = false;
    hasPolicyWithEnhancedManagedBenefits = false;

    benefitPlans: BenefitPlan[];

    hasBenefitsPayroll = false;

    constructor(
        protected route: ActivatedRoute,
        protected notify: NotifyService,
        public file: FileHelperService,
        private analyticsService: AnalyticService,
        private featureService: FeatureService
    ) {
        this.employee = this.route.snapshot.data.models.get('employee');
        this.resolve();
    }

    async ngOnInit(): Promise<void> {
        const [[benefitPlans], hasBenefitsPayroll] = await Promise.all([
            BenefitPlan.param('company', this.employee.companyId).all(),
            this.featureService.has(FeatureFlag.benefitsPayroll),
        ]);

        this.benefitPlans = benefitPlans;
        this.hasBenefitsPayroll = hasBenefitsPayroll;

        this.hasPolicyWithEnhancedManagedBenefits = benefitPlans.find(
            (benefitPlan) => benefitPlan?.benefitPolicy.hasEnhancedManagedBenefits === true
        );

        this.setEditFlag();
    }

    onSave(): void {
        if (!this.employeeBenefitPlan.isDirty()) {
            return;
        }
        if (this.employeeBenefitPlan.benefitPlanId === null && this.employeeBenefitPlan.id !== null) {
            this.onDelete();
            return;
        }
        if (this.employeeBenefitPlan.benefitPlanId === null) {
            return;
        }
        this.isLoading.next(true);
        this.employeeBenefitPlan
            .param('employee', this.employee.id)
            .save()
            .then(() => {
                // Add pendo tracking
                this.addPendoTrackingForNewAndExistingBenefits();

                this.notify.success('employees.benefits.saved');
                this.resolve();
            })
            .catch((err) => {
                this.notify.error(err.message || 'employees.benefits.couldNotSave');
            });
    }

    onDelete(): void {
        this.isLoading.next(true);
        this.employeeBenefitPlan
            .param('employee', this.employee.id)
            .delete()
            .then(() => {
                this.notify.success('employees.benefits.saved');
                this.resolve();
            })
            .catch((err) => {
                this.notify.error(err.message || 'employees.benefits.couldNotSave');
            });
    }

    onDownloadPlan(plan: BenefitPlan): void {
        this.file.save(plan.fileId, plan.name, '.pdf');
    }

    onDownloadBenefit(benefitPlan: BenefitPlan, benefit: Benefit): void {
        const fileName = benefitPlan.name + (benefit.benefitCategory ? ' - ' + benefit.benefitCategory.name : '');

        this.file.save(benefit.fileId, fileName, '.pdf');
    }

    onShowDisclaimer(): void {
        if (this.employeeBenefitPlan?.benefitPlan?.hasEnhancedManagedBenefits) {
            this.editBenefitsModal.show();
            return;
        }

        this.disclaimerDialog.show().then((yes: boolean) => {
            if (yes) {
                this.editBenefitsModal.show();
            }
        });
    }

    addPendoTrackingForClickCarrierPortal(): void {
        this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfileBenefitsTabClickCarrierPortal);
    }

    private resolve(): void {
        EmployeeBenefitPlan.param('employee', this.employee.id)
            .with([
                'benefitPlan',
                'benefitDivision',
                'benefitPlan.file',
                'benefitPlan.benefitProvider',
                'benefitPlan.benefits',
                'benefitPlan.benefits.benefitProvider',
                'benefitPlan.benefits.file',
                'benefitPlan.benefits.benefitCategory',
                'benefitPlan.benefits.benefitDetails',
                'benefitPlan.benefits.benefitDetails.benefitType',
                'overrides',
            ])
            .all()
            .then(([benefitPlans]) => {
                this.isUserUpdatingExistingPlan = !!benefitPlans.length;

                if (!benefitPlans.length) {
                    this.employeeBenefitPlan = new EmployeeBenefitPlan({
                        employeeId: this.employee.id,
                    });

                    this.setEditFlag();
                    this.isLoading.next(false);
                    return;
                }

                // One to many however we only support one to one in the UI
                this.employeeBenefitPlan = benefitPlans[0];
                this.shouldShowCard = this.employeeBenefitPlan?.shouldShowCard();

                this.setEditFlag();
                this.isLoading.next(false);
            });
    }

    private setEditFlag(): void {
        // We only allow edit through this flow if the user has a plan already assigned or if the company
        // has no plans with a B+ policy.
        // If the user already has a plan assigned, the child form component will prevent plan switching.
        this.allowEdit =
            !!this.editPermission.length &&
            (!this.hasPolicyWithEnhancedManagedBenefits || this.employeeBenefitPlan.isPersisted);
    }

    private addPendoTrackingForNewAndExistingBenefits(): void {
        if (this.isUserUpdatingExistingPlan) {
            this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfileBenefitsTabEditExistingPlan);
        } else {
            this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfileBenefitsTabCreateNewPlan);
        }
    }
}
